import { DateTime } from 'luxon';
import uniqBy from 'lodash.uniqby';
import { dueDateRangeForPreset, DueDatePreset } from 'components/filters/dueDatePreset';
import { taskEstimatedRemainingMinutes } from 'components/tasks/TaskPane/timeLog';
import { CLOSED_TASK_STATUSES, isOpenTaskStatus, Task, TaskDateRangeFilter, TaskImportance, TaskListFilter } from 'services/tasks/types';
import { isDueDatePast, toDateTime, userNow } from 'utils/date';
import { getUserTimeZone } from 'utils/timezone';
import { applyRangeSelection, getItemsInRange } from 'utils/rangeSelection';

const MAX_BATCH_SIZE = 50;
const UNASSIGNED_FILTER_VALUE = 'unassigned';

type TaskCompletionFilter = 'ALL' | 'ARCHIVED' | 'CANCELLED' | 'COMPLETE' | 'INCOMPLETE';

type TaskImportanceFilter = 'ALL' | 'NONE' | TaskImportance;

interface TasksFilterDraft {
  assignees: string[];
  completion: TaskCompletionFilter;
  dueDate: DueDatePreset;
  importance: TaskImportanceFilter;
  includeArchived?: boolean;
}

const DEFAULT_TASKS_FILTER_DRAFT: TasksFilterDraft = {
  assignees: [],
  completion: 'INCOMPLETE',
  dueDate: 'ALL',
  importance: 'ALL',
};

interface TasksViewFilter {
  archived?: boolean;
  assignees?: string[];
  cancelled?: boolean;
  complete?: boolean;
  dueAt?: TaskDateRangeFilter;
  importance?: TaskImportance | null;
  includeArchived?: boolean;
  missingDueAt?: boolean;
}

const toTasksViewFilter = (
  draft: TasksFilterDraft,
  now: DateTime = userNow(),
): TasksViewFilter => {
  const filter: TasksViewFilter = {
    assignees: draft.assignees,
  };

  if (draft.completion === 'ARCHIVED') {
    filter.archived = true;
  } else if (draft.completion === 'CANCELLED') {
    filter.archived = false;
    filter.cancelled = true;
  } else if (draft.completion === 'COMPLETE') {
    filter.archived = false;
    filter.complete = true;
  } else if (draft.completion === 'INCOMPLETE') {
    filter.archived = false;
    filter.complete = false;
  } else if (draft.includeArchived) {
    filter.includeArchived = true;
  } else {
    filter.archived = false;
  }

  const dueAt = dueDateRangeForPreset(draft.dueDate, now);
  if (dueAt === null) {
    filter.missingDueAt = true;
  } else if (dueAt) {
    filter.dueAt = dueAt;
  }

  if (draft.importance === 'NONE') {
    filter.importance = null;
  } else if (draft.importance !== 'ALL') {
    filter.importance = draft.importance;
  }

  return filter;
};

const toServerTaskFilter = (
  viewFilter: TasksViewFilter = {},
  extraFilter: TaskListFilter = {},
): TaskListFilter => {
  const filter: TaskListFilter = { ...extraFilter };

  if (!viewFilter.includeArchived) {
    filter.isArchived = !!viewFilter.archived;
  }

  if (viewFilter.cancelled) {
    filter.status = 'Cancelled';
  } else if (viewFilter.complete === true) {
    filter.status = 'Completed';
  } else if (viewFilter.complete === false) {
    filter.status = { $nin: [...CLOSED_TASK_STATUSES] };
  }

  if (viewFilter.missingDueAt) {
    filter.dueAt = null;
  } else if (viewFilter.dueAt) {
    filter.dueAt = viewFilter.dueAt;
  }

  if (viewFilter.importance !== undefined) {
    filter.importance = viewFilter.importance;
  }

  if (viewFilter.assignees?.length) {
    const includeUnassigned = viewFilter.assignees.includes(UNASSIGNED_FILTER_VALUE);
    const ids = viewFilter.assignees.filter(id => id !== UNASSIGNED_FILTER_VALUE);
    const assigneeValues: Array<string | null> = [
      ...ids,
      ...(includeUnassigned ? [null, ''] : []),
    ];
    filter.assignee = assigneeValues.length === 1
      ? assigneeValues[0] as string
      : assigneeValues;
  }

  return filter;
};

const endOfLocalDay = (dateTime: DateTime) => (
  dateTime.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
);

const isDateTimeInDueAtFilter = (
  dateTime: DateTime,
  dueAt: TaskDateRangeFilter,
) => {
  const dueAtGt = dueAt.$gt && DateTime.fromISO(dueAt.$gt);
  const dueAtGte = dueAt.$gte && DateTime.fromISO(dueAt.$gte);
  const dueAtLt = dueAt.$lt && DateTime.fromISO(dueAt.$lt);
  const dueAtLte = dueAt.$lte && DateTime.fromISO(dueAt.$lte);
  if (dueAtGt && dateTime <= dueAtGt) return false;
  if (dueAtGte && dateTime < dueAtGte) return false;
  if (dueAtLt && dateTime >= dueAtLt) return false;
  if (dueAtLte && dateTime > dueAtLte) return false;
  return true;
};

const dueAtFromListFilter = (
  dueAt: string | TaskDateRangeFilter,
  now: DateTime,
): Date | undefined => {
  if (typeof dueAt === 'string') {
    return endOfLocalDay(DateTime.fromISO(dueAt).setZone(getUserTimeZone())).toJSDate();
  }

  const rangeStartIso = dueAt.$gte || dueAt.$gt;
  const rangeEndIso = dueAt.$lt || dueAt.$lte;
  if (!rangeStartIso && !rangeEndIso) {
    return undefined;
  }

  const candidate = isDateTimeInDueAtFilter(now.toUTC(), dueAt)
    ? now
    : (rangeStartIso
      ? DateTime.fromISO(rangeStartIso).setZone(getUserTimeZone())
      : DateTime.fromISO(rangeEndIso!).setZone(getUserTimeZone()).minus({ milliseconds: 1 }));

  return endOfLocalDay(candidate).toJSDate();
};

const projectIdsFromListFilter = (
  projects?: string | string[],
  projectId?: string,
) => (
  [...new Set([
    ...(projectId ? [projectId] : []),
    ...(typeof projects === 'string' ? [projects] : (projects || [])),
  ])]
);

interface CreateTaskDefaultsOptions {
  currentUserId?: string;
  now?: DateTime;
  projectId?: string;
}

const toCreateTaskDefaults = (
  filter: TaskListFilter = {},
  options: CreateTaskDefaultsOptions = {},
): Partial<Pick<Task, 'assignee' | 'dueAt' | 'importance' | 'projects'>> => {
  const defaults: Partial<Pick<Task, 'assignee' | 'dueAt' | 'importance' | 'projects'>> = {};
  const projectIds = projectIdsFromListFilter(filter.projects, options.projectId);
  if (projectIds.length) {
    defaults.projects = projectIds.map(id => ({ id }));
  }

  if (filter.dueAt) {
    const dueAt = dueAtFromListFilter(filter.dueAt, options.now ?? userNow());
    if (dueAt) {
      defaults.dueAt = dueAt;
    }
  }

  if (typeof filter.importance === 'string') {
    defaults.importance = filter.importance;
  }

  const filterAssignee = Array.isArray(filter.assignee)
    ? filter.assignee[0]
    : filter.assignee;
  if (filterAssignee) {
    defaults.assignee = filterAssignee;
  } else if (defaults.dueAt && options.currentUserId) {
    defaults.assignee = options.currentUserId;
  }

  return defaults;
};

const plannedMinutes = (task): number | undefined => {
  const minutes = taskEstimatedRemainingMinutes(task);
  return typeof minutes === 'number' && minutes > 0 ? minutes : undefined;
};

const calculateAverageCompletionTime = tasks => {
  const tasksWithNonNullEstimatedCompletionTime = tasks?.filter(task => plannedMinutes(task));
  if (!tasksWithNonNullEstimatedCompletionTime || tasksWithNonNullEstimatedCompletionTime.length === 0) {
    return null;
  }
  return (tasksWithNonNullEstimatedCompletionTime
    .reduce((acc, task) => (acc + plannedMinutes(task)), 0)) / tasksWithNonNullEstimatedCompletionTime.length;
};

const calculateCompleteTime = tasks => {
  const complete = tasks.filter(task => task.status === 'Completed');
  const averageEstimatedCompletionTime = calculateAverageCompletionTime(tasks);
  return complete
    .reduce((acc, task) => (acc + (plannedMinutes(task) || averageEstimatedCompletionTime)), 0);
};

const calculateIncompleteTime = tasks => {
  const incomplete = tasks?.filter(task => isOpenTaskStatus(task.status));
  const averageEstimatedCompletionTime = calculateAverageCompletionTime(tasks);
  return incomplete?.reduce((acc, task) => (acc + (plannedMinutes(task) || averageEstimatedCompletionTime)), 0);
};

const calculateCompletionPercentage = tasks => {
  if (!tasks || tasks.length === 0) {
    return null;
  }
  const complete = tasks.filter(task => task.status === 'Completed');
  const incomplete = tasks.filter(task => isOpenTaskStatus(task.status));
  if (complete.length + incomplete.length === 0) {
    return null;
  }
  const tasksWithNonNullEstimatedCompletionTime = tasks.filter(task => plannedMinutes(task));
  if (tasksWithNonNullEstimatedCompletionTime.length === 0) {
    return complete.length / (complete.length + incomplete.length);
  }
  const completeTime = calculateCompleteTime(tasks);
  const incompleteTime = calculateIncompleteTime(tasks);
  return completeTime / (completeTime + incompleteTime);
};

const compareTaskDescriptions = (t1: Task, t2: Task) => (
  (t1.description || '').toLowerCase().localeCompare((t2.description || '').toLowerCase())
);

const sortsAfterOpenTasks = (task: Task) => (
  !!task.isArchived || !isOpenTaskStatus(task.status)
);

const sortTasks = (tasks: Task[] = []): Task[] => (
  [...tasks].sort((t1, t2) => {
    const trailing1 = sortsAfterOpenTasks(t1) ? 1 : 0;
    const trailing2 = sortsAfterOpenTasks(t2) ? 1 : 0;
    if (trailing1 !== trailing2) return trailing1 - trailing2;
    if (!t1.dueAt && !t2.dueAt) return compareTaskDescriptions(t1, t2);
    if (!t1.dueAt) return 1;
    if (!t2.dueAt) return -1;
    if (t1.dueAt < t2.dueAt) return -1;
    if (t1.dueAt > t2.dueAt) return 1;
    return compareTaskDescriptions(t1, t2);
  })
);

export interface NestedTask {
  depth: number;
  parent?: Task;
  previousSibling?: Task;
  task: Task;
}

const nestTasks = (tasks: Task[] = []): NestedTask[] => {
  const byId = new Map(
    tasks
      .filter((task): task is Task & { id: string } => !!task.id)
      .map(task => [task.id, task]),
  );
  const childrenByParent = new Map<string, Task[]>();
  const roots: Task[] = [];

  for (const task of tasks) {
    const parentId = task.parentTaskId;
    if (parentId && byId.has(parentId)) {
      const children = childrenByParent.get(parentId) ?? [];
      children.push(task);
      childrenByParent.set(parentId, children);
    } else {
      roots.push(task);
    }
  }

  const result: NestedTask[] = [];
  const visiting = new Set<string>();
  const rendered = new Set<string>();

  const walk = (task: Task, depth: number, parent?: Task, previousSibling?: Task) => {
    if (task.id && visiting.has(task.id)) {
      return;
    }
    if (task.id) {
      visiting.add(task.id);
    }
    result.push({
      depth,
      parent,
      previousSibling,
      task,
    });
    if (task.id) {
      rendered.add(task.id);
      const children = childrenByParent.get(task.id) || [];
      let previousChild: Task | undefined;
      for (const child of children) {
        walk(child, depth + 1, task, previousChild);
        previousChild = child;
      }
      visiting.delete(task.id);
    }
  };

  let previousRoot: Task | undefined;
  for (const root of roots) {
    walk(root, 0, undefined, previousRoot);
    previousRoot = root;
  }

  for (const task of tasks) {
    if (task.id && !rendered.has(task.id)) {
      walk(task, 0);
    }
  }

  return result;
};

const getTasksInRange = (tasks: Task[], fromId: string, toId: string): Task[] => (
  getItemsInRange(tasks, fromId, toId)
);

interface ApplyTaskSelectionParams {
  displayedTasks: Task[];
  lastSelectedTaskId?: string;
  selectedTasks: Task[];
  shiftKey: boolean;
  task: Task;
}

const applyTaskSelection = ({
  displayedTasks,
  lastSelectedTaskId,
  selectedTasks,
  shiftKey,
  task,
}: ApplyTaskSelectionParams): {
  lastSelectedTaskId?: string;
  selectedTasks: Task[];
} => {
  const result = applyRangeSelection({
    displayedItems: displayedTasks,
    item: task,
    lastSelectedId: lastSelectedTaskId,
    selectedItems: selectedTasks,
    shiftKey,
  });
  return {
    lastSelectedTaskId: result.lastSelectedId,
    selectedTasks: result.selectedItems,
  };
};

const taskMatchesAssignees = (task: Task, assignees?: string[]) => {
  if (!assignees?.length) {
    return true;
  }
  const includeUnassigned = assignees.includes(UNASSIGNED_FILTER_VALUE);
  const ids = assignees.filter(id => id !== UNASSIGNED_FILTER_VALUE);
  if (includeUnassigned && !task.assignee) {
    return true;
  }
  return !!task.assignee && ids.includes(task.assignee);
};

const filterTasks = (tasks, filter) => tasks?.filter(task => {
  if (!filter.includeArchived) {
    if (task.isArchived) {
      return !!filter.archived;
    }
    if (filter.archived) {
      return false;
    }
  }
  if (filter.cancelled) {
    return task.status === 'Cancelled';
  }
  if (filter.complete === true) {
    return task.status === 'Completed';
  }
  if (filter.complete === false) {
    return isOpenTaskStatus(task.status);
  }
  return true;
})
  .filter(task => {
    if (filter.importance === undefined) {
      return true;
    }
    if (filter.importance === null) {
      return !task.importance;
    }
    return task.importance === filter.importance;
  })
  .filter(task => taskMatchesAssignees(task, filter.assignees))
  .filter(task => {
    if (filter.missingDueAt) {
      return !task.dueAt;
    }
    const dueAtGt = filter.dueAt?.$gt && DateTime.fromISO(filter.dueAt.$gt);
    const dueAtGte = filter.dueAt?.$gte && DateTime.fromISO(filter.dueAt.$gte);
    const dueAtLt = filter.dueAt?.$lt && DateTime.fromISO(filter.dueAt.$lt);
    const dueAtLte = filter.dueAt?.$lte && DateTime.fromISO(filter.dueAt.$lte);
    const dueAt = toDateTime(task.dueAt);
    if (!dueAt) return !filter.dueAt;
    if (dueAtGt && dueAt <= dueAtGt) return false;
    if (dueAtGte && dueAt < dueAtGte) return false;
    if (dueAtLt && dueAt >= dueAtLt) return false;
    if (dueAtLte && dueAt > dueAtLte) return false;
    return true;
  });

const getFirstDueDate = tasks => {
  return tasks
    .reduce((acc, task) => ((acc && task.startAt < acc) ? acc : task.startAt || null), null);
};

const getFurthestDueDate = tasks => {
  return tasks
    .reduce((acc, task) => ((acc && acc < task.dueAt) ? acc : task.dueAt || null), null);
};

const getLateTasks = tasks => {
  return tasks
    .filter(task => !task.isArchived)
    .filter(task => isOpenTaskStatus(task.status) && isDueDatePast(task.dueAt));
};

const calculateLatenessRatio = tasks => {
  if (tasks.length === 0) {
    return null;
  }

  // TODO
  /*
  const hasdueAtPassed = task
    && task.status !== 'Completed'
    && task.dueAt && DateTime.fromISO(task.dueAt) < DateTime.now();

  const tasksWithNonNullDueAt = tasks.filter(task => task.dueAt);

  const complete = tasks.filter(task => task.status === 'Completed');
  const incomplete = tasks.filter(task => task.status !== 'Completed');
  if (tasksWithNonNullEstimatedCompletionTime.length === 0) {
    return complete.length / (complete.length + incomplete.length);
  }
  const averageEstimatedCompletionTime = (tasksWithNonNullEstimatedCompletionTime
    .reduce((acc, task) => (acc + task.estimatedCompletionTime), 0)) / tasksWithNonNullEstimatedCompletionTime.length;
  const completeTime = complete
    .reduce((acc, task) => (acc + (task.estimatedCompletionTime || averageEstimatedCompletionTime)), 0);
  const incompleteTime = incomplete
    .reduce((acc, task) => (acc + (task.estimatedCompletionTime || averageEstimatedCompletionTime)), 0);
  return completeTime / (completeTime + incompleteTime);
  */
};

const calculateOverloadInTheFuture = (tasks: Task[]) => {
  const MAX_NB_WORK_HOURS_PER_DAY = 4;
  const allTasksDueInTheFuture = tasks?.filter(t1 => t1.dueAt && isOpenTaskStatus(t1.status))
    .sort((a, b) => (a.dueAt! < b.dueAt! ? -1 : 1));
  const uniqueDueDates = uniqBy(allTasksDueInTheFuture, t1 => toDateTime(t1.dueAt)!.toISODate())
    .map(t1 => toDateTime(t1.dueAt)!.set({ hour: 23, minute: 59, second: 59, millisecond: 999 }));

  let isOverloadedInTheFuture: any = null;
  for (const dueDate of uniqueDueDates) {
    const nbDaysBeforeDate = dueDate.diff(
      userNow().startOf('day'),
      'days',
    ).days;
    const amountHoursBeforeDate = allTasksDueInTheFuture?.filter(t1 => 
        (toDateTime(t1.dueAt)! <= dueDate && plannedMinutes(t1))
      )
      .map(t1 => plannedMinutes(t1)! / 60)
      .reduce((acc, curr) => (acc! + curr!), 0);
    const averageHoursPerDayBeforeDate = amountHoursBeforeDate
      && nbDaysBeforeDate
      && (amountHoursBeforeDate / nbDaysBeforeDate);
    if (averageHoursPerDayBeforeDate > MAX_NB_WORK_HOURS_PER_DAY) {
      isOverloadedInTheFuture = {
        date: dueDate,
        averageHoursPerDayBeforeDate,
      };
      break;
    }
  }
  return isOverloadedInTheFuture;
};

const calculateTasksDueTooLate = (tasks, projects) => {
  const tasksDueTooLate = tasks
    .map(t => {
      const taskProjects = (t.projects || [])
        .map(({ id }) => projects.find(p => p.id === id));
      const projectsDueBefore = taskProjects.filter(p => t.dueAt && p.dueAt && p.dueAt < t.dueAt);
      return {
        ...t,
        projectsDueBefore,
      };
    })
    .filter(t => t.projectsDueBefore.length);
  return tasksDueTooLate
    .reduce((acc, task) => ([
      ...acc,
      ...task.projectsDueBefore
        .map(p => ({
          task,
          project: p,
        })),
    ]), []);
};

const calculateTasksWithNoDueDate = tasks => {
  return tasks
    .filter(t => isOpenTaskStatus(t.status) && !t.dueAt);
};

export {
  applyTaskSelection,
  calculateCompleteTime,
  calculateIncompleteTime,
  calculateCompletionPercentage,
  DEFAULT_TASKS_FILTER_DRAFT,
  filterTasks,
  getFirstDueDate,
  getFurthestDueDate,
  getLateTasks,
  getTasksInRange,
  nestTasks,
  calculateLatenessRatio,
  calculateOverloadInTheFuture,
  calculateTasksDueTooLate,
  calculateTasksWithNoDueDate,
  MAX_BATCH_SIZE,
  sortTasks,
  toCreateTaskDefaults,
  toServerTaskFilter,
  toTasksViewFilter,
  UNASSIGNED_FILTER_VALUE,
};

export type {
  TasksFilterDraft,
  TasksViewFilter,
};
